import Header from "../../../components/header";
import { X } from "lucide-react";
import ConfigCard from "./ConfigCard";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axios";
import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useUnsavedChangesWarning } from "../../../hooks/useNavigationBlocker";

const EditUserConfig = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [unsavedChanges, setunsavedChanges] = useState({});
  const [error, setError] = useState({});
  
  // Check if there are any unsaved changes across all stations
  const isDirty = Object.values(unsavedChanges).some(v => v === true);
  useUnsavedChangesWarning(isDirty);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`origo/get-org-data/${organizationId}`);
      setData(res.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionToggle = (tenantId) => {
    setOpenAccordion(openAccordion === tenantId ? null : tenantId);
  };

  const handleSave = async (checkedSensorIds, uncheckedSensorIds, aggregationData, title, payload) => {
    try {
      const promises = [];

      // Handle sensor toggle
      checkedSensorIds.length && promises.push(api.put('origo/toggle-sensors', {
          ...payload,
          sensors: checkedSensorIds,
          toggle: true
        }));
      
      uncheckedSensorIds.length && promises.push(api.put('origo/toggle-sensors', {
          ...payload,
          sensors: uncheckedSensorIds,
          toggle: false  
        }));

      // Handle aggregation data if provided
      if (aggregationData && aggregationData.length > 0) {
        promises.push(api.put('config/set-aggregation-types ', 
          aggregationData
        ));
      }

      await Promise.all(promises);
      
      // Update the local data state to reflect the saved changes
      setData(prevData => {
        const newData = { ...prevData };
        const tenant = newData.tenants.find(t => t.id === payload.tenant_id);
        if (tenant) {
          const station = tenant.stations.find(s => s.id === payload.station_id);
          if (station) {
            // Update sensor active states
            station.sensors.forEach(sensor => {
              if (checkedSensorIds.includes(sensor.id)) {
                sensor.is_active = true;
              } else if (uncheckedSensorIds.includes(sensor.id)) {
                sensor.is_active = false;
              }
              
              // Update aggregation types
              const aggregationItem = aggregationData?.find(item => item.sensor_id === sensor.id);
              if (aggregationItem) {
                sensor.user_selected_aggregation_types = aggregationItem.aggregation_types;
              }
            });
          }
        }
        return newData;
      });

      toast.success(`${title} updated successfully!`);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Save failed. Please try again.');
    }
  };

  const handleEditLabel = async (id, label, original_title, sIdx, tIdx) => {
    try {
      await api.post(`config/set-user-defined-title`, 
        { 
          sensor_title:original_title,
          organization_id: Number(organizationId),
          user_defined_title: label} 
      );
      await fetchData();
      // setData(prev => {
      //   prev.tenants[tIdx].stations[sIdx].sensors.find(s => s.id===id).origo_sensor_name = label
      //   return {...prev}
      // })
      // toast.success("Label updated successfully!");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Update failed. Please try again.");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    setError({...unsavedChanges})
  }, [unsavedChanges]);

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 dark:bg-gray-900 transition-colors duration-200">
        <Header />
        <div className="flex justify-center items-center h-96">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 dark:bg-gray-900 transition-colors duration-200">
      <Header />

      {/* Content */}
      <div className="p-10">
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-col">
            <h2 className="text-lg font-normal text-gray-900 dark:text-white">
              Edit Configuration
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{data.name}</p>
          </div>

          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-2 rounded-md px-2 py-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 h-max transition-colors duration-200">
              <span className="badge badge-success badge-xs"></span>
              <span className="font-medium text-gray-700 dark:text-gray-300 text-xs">
                {`Active ${data.tenants.filter(t => t.is_active).length} / ${data.tenants.length}`}
              </span>
            </div>
            {/* <button className="h-max btn btn-secondary dark:bg-blue-600 dark:hover:bg-blue-700 rounded-full capitalize font-normal px-4 py-1 text-xs text-white transition-colors duration-200">
              <Check className="w-4 h-4" />
              Update Data
            </button> */}
            <button
              className="h-max btn bg-gray-400 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full capitalize font-normal px-4 py-1 text-xs text-white transition-colors duration-200"
              onClick={() => {
                if(isDirty){
                  if(window.confirm(`You have unsaved changes. Are you sure you want to leave?`)){
                    navigate(-1)
                  }else{
                    return;
                  }
                } else {
                  navigate(-1)
                }
              }}
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>

        {/* Tenant Accordions */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg dark:shadow-gray-900/30 transition-colors duration-200">
          {data?.tenants.map((tenant, tIdx) => (
            <div key={tenant.id} className="collapse collapse-arrow border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
              <input 
                type="checkbox" 
                checked={openAccordion === tenant.id}
                onChange={() => handleAccordionToggle(tenant.id)}
              />
              <div className="collapse-title text-lg font-medium bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                {tenant.origo_tenant_name}
                <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                  ({tenant.stations.filter(station => station.sensors && station.sensors.length > 0).length} stations)
                </span>
              </div>
              <div className="collapse-content bg-gray-50 dark:bg-gray-700">
                <div className="flex flex-wrap justify-around gap-6 pt-4">
                  {tenant.stations
                    .filter(station => station.sensors && station.sensors.length > 0)
                    .map((station, sIdx) => (
                    <ConfigCard
                      key={station.id}
                      title={station.origo_station_name}
                      total={station.sensors.length}
                      selected={station.sensors.filter((s) => s.is_active).length}
                      options={station.sensors.map((s) => {
                        const availableTypes = s?.aggregation_types || [];
                        const userSelectedTypes = s?.user_selected_aggregation_types;
                        
                        let aggregationTypesMap = [];
                        
                        if (userSelectedTypes === null || userSelectedTypes === undefined) {
                          // If user_selected_aggregation_types is null, all aggregation_types should be true
                          aggregationTypesMap = availableTypes.map(type => ({[type]: true}));
                        } else {
                          // Normal case: available types default to false, user selected types are true
                          aggregationTypesMap = [
                            ...availableTypes.map(type => ({[type]: false})),
                            ...(userSelectedTypes || []).map(type => ({[type]: true})),
                          ];
                        }
                        
                        return {
                          id: s.id,
                          label: s.origo_sensor_user_defined_title || s.origo_sensor_name,
                          checked: s.is_active,
                          original_title: s.origo_sensor_name,
                          aggregation_types: aggregationTypesMap,
                          available_aggregation_types: availableTypes,
                          has_single_aggregation: availableTypes.length === 1,
                        };
                      })}
                      stationData={{
                        organization_id: organizationId,
                        tenant_id: tenant.id,
                        station_id: station.id,
                      }}
                      onSave={(checkedOptions, uncheckedOptions, aggregationData) => {
                        handleSave(
                            checkedOptions, 
                            uncheckedOptions,
                            aggregationData,
                            station.origo_station_name, 
                            {
                              organization_id: Number(organizationId),
                              tenant_id: tenant.id,
                              station_id: station.id,
                            })
                      }}
                      onEditLabel={(id, label, original_title) => {
                          handleEditLabel(id, label, original_title, sIdx, tIdx)
                        }}
                      setunsavedChanges={setunsavedChanges}
                      showError={Object.keys(error).includes(station.id.toString()) && error[station.id]}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EditUserConfig;
